"use client";

import type { JSX } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  basePath: string;
  className?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  basePath,
  className,
}: PaginationProps): JSX.Element | null {
  // Build a clean URL — only the page param, no inherited query string.
  // Carrying unknown params (e.g. __clerk_handshake) through pagination links
  // embeds stale tokens in every page URL and causes hydration errors.
  const createPageUrl = (page: number): string => {
    if (page <= 1) return basePath;
    return `${basePath}?page=${page}`;
  };

  // Generate page numbers to display
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 7; // Show up to 7 page numbers

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("ellipsis-start");
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("ellipsis-end");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  if (totalPages <= 1) {
    return null; // Don't show pagination if only one page
  }

  return (
    <nav
      aria-label="Pagination"
      className={cn("flex items-center justify-center gap-2", className)}
    >
      {/* Previous Button */}
      <Button
        asChild
        variant="outline"
        size="sm"
        disabled={currentPage === 1}
        className={cn(
          "min-w-[2.5rem]",
          currentPage === 1 && "pointer-events-none opacity-50"
        )}
      >
        {currentPage === 1 ? (
          <span aria-disabled="true">Previous</span>
        ) : (
          <Link href={createPageUrl(currentPage - 1)}>Previous</Link>
        )}
      </Button>

      {/* Page Numbers */}
      <div className="flex items-center gap-1">
        {pageNumbers.map((page, index) => {
          if (page === "ellipsis-start" || page === "ellipsis-end") {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-2 py-1 text-sm text-gray-500"
                aria-hidden="true"
              >
                ...
              </span>
            );
          }

          const pageNum = page as number;
          const isActive = pageNum === currentPage;

          return (
            <Button
              key={pageNum}
              asChild
              variant={isActive ? "default" : "outline"}
              size="sm"
              className={cn(
                "min-w-[2.5rem]",
                isActive && "pointer-events-none"
              )}
              aria-current={isActive ? "page" : undefined}
              aria-label={`Page ${pageNum}${isActive ? ", current page" : ""}`}
            >
              {isActive ? (
                <span>{pageNum}</span>
              ) : (
                <Link href={createPageUrl(pageNum)}>{pageNum}</Link>
              )}
            </Button>
          );
        })}
      </div>

      {/* Next Button */}
      <Button
        asChild
        variant="outline"
        size="sm"
        disabled={currentPage === totalPages}
        className={cn(
          "min-w-[2.5rem]",
          currentPage === totalPages && "pointer-events-none opacity-50"
        )}
      >
        {currentPage === totalPages ? (
          <span aria-disabled="true">Next</span>
        ) : (
          <Link href={createPageUrl(currentPage + 1)}>Next</Link>
        )}
      </Button>
    </nav>
  );
}
